import React, { useCallback } from 'react';
import Contact from '../../../models/Contact';
import { CreateInvestmentEntityRequest, InvestmentEntity } from '../../../models/InvestmentEntity';
import Investor from '../../../models/Investor';
import {
  ASSOCIATION_TIMEOUT_MS,
  createAndAssociateEntity,
} from './adminEntityAssociationWizardActions';
import {
  allocationSuccessMessage,
  persistEntityAllocation,
} from './adminEntityAssociationWizardHandlers';
import { mapWizardStructuredError } from './adminEntityAssociationWizardErrors';
import { AdminEntityAssociationWizardState, WizardStep } from './adminEntityAssociationWizardTypes';

type RefreshResult = {
  investor: Investor;
  allocations: AdminEntityAssociationWizardState['currentAllocations'];
} | null;

interface UseAdminEntityAssociationMutationsParams {
  state: AdminEntityAssociationWizardState;
  setState: React.Dispatch<React.SetStateAction<AdminEntityAssociationWizardState>>;
  setMessage: (message: { type: 'error' | 'success'; text: string }) => void;
  goToStep: (step: WizardStep, partial?: Partial<AdminEntityAssociationWizardState>) => void;
  loadEntities: (
    contact: Contact | null,
    investor: Investor | null,
    opts?: { navigateOnInit?: boolean; silent?: boolean },
  ) => Promise<void>;
  refreshInvestorData: () => Promise<RefreshResult>;
  refreshActions: () => void | Promise<void>;
}

/**
 * Create/associate mutation handlers for the entity-association wizard. Extracted
 * from useAdminEntityAssociationWizard to keep that hook focused on state and step
 * navigation; these two handlers share the same persist → refresh → navigate shape.
 */
export function useAdminEntityAssociationMutations({
  state,
  setState,
  setMessage,
  goToStep,
  loadEntities,
  refreshInvestorData,
  refreshActions,
}: UseAdminEntityAssociationMutationsParams) {
  const handleEntityCreated = useCallback(
    async (entityData: CreateInvestmentEntityRequest) => {
      if (!state.investor) return;

      await refreshInvestorData();

      try {
        setState((prev) => ({
          ...prev,
          loading: true,
          error: null,
          validationErrors: {},
        }));

        const investmentEntity = await createAndAssociateEntity(state.investor.id, entityData);
        const updated = await refreshInvestorData();

        await loadEntities(state.contact, updated?.investor ?? state.investor, { silent: true });

        goToStep('allocation', {
          selectedEntity: investmentEntity,
          investor: updated?.investor ?? state.investor,
          currentAllocations: updated?.allocations ?? state.currentAllocations,
          loading: false,
        });

        setMessage({
          type: 'success',
          text: 'Investment entity created successfully',
        });
      } catch (error) {
        console.error(error);
        const mapped = mapWizardStructuredError(error);
        if (mapped.validationErrors) {
          setState((prev) => ({
            ...prev,
            loading: false,
            validationErrors: mapped.validationErrors,
          }));
        } else {
          setState((prev) => ({ ...prev, loading: false }));
          setMessage({
            type: 'error',
            text: mapped.message || 'Failed to create investment entity',
          });
        }
      } finally {
        await refreshActions();
      }
    },
    [
      goToStep,
      loadEntities,
      refreshActions,
      refreshInvestorData,
      setMessage,
      setState,
      state.contact,
      state.currentAllocations,
      state.investor,
    ],
  );

  const associateEntityWithAllocation = useCallback(
    async (entity: InvestmentEntity, allocatedAmount: number) => {
      if (!state.investor) return;

      const timeoutId = setTimeout(() => {
        setState((prev) => ({ ...prev, loading: false }));
        setMessage({ type: 'error', text: 'Operation timed out. Please try again.' });
      }, ASSOCIATION_TIMEOUT_MS);

      try {
        setState((prev) => ({
          ...prev,
          loading: true,
          error: null,
          validationErrors: {},
        }));

        const wasUpdate = await persistEntityAllocation(
          state.investor,
          entity,
          allocatedAmount,
          state.currentAllocations,
        );

        const updated = await refreshInvestorData();
        const updatedInvestor = updated?.investor ?? state.investor;
        const updatedAllocations = updated?.allocations ?? state.currentAllocations ?? [];

        setMessage({ type: 'success', text: allocationSuccessMessage(wasUpdate) });

        clearTimeout(timeoutId);
        await loadEntities(state.contact, updatedInvestor, { silent: true });

        goToStep('contacts', {
          selectedEntity: entity,
          investor: updatedInvestor,
          currentAllocations: updatedAllocations,
          loading: false,
        });
      } catch (error) {
        clearTimeout(timeoutId);
        console.error(error);
        const mapped = mapWizardStructuredError(error);
        if (mapped.validationErrors) {
          setState((prev) => ({
            ...prev,
            loading: false,
            validationErrors: mapped.validationErrors,
          }));
        } else {
          setState((prev) => ({ ...prev, loading: false }));
          setMessage({
            type: 'error',
            text: mapped.message || 'Failed to associate entity with allocation',
          });
        }
      } finally {
        await refreshActions();
      }
    },
    [
      goToStep,
      loadEntities,
      refreshActions,
      refreshInvestorData,
      setMessage,
      setState,
      state.contact,
      state.currentAllocations,
      state.investor,
    ],
  );

  return { handleEntityCreated, associateEntityWithAllocation };
}
